<script setup>
import RemakeHeader from '@/__core__/components/headers/remake-header/RemakeHeader.vue'
import Tabs from '@/__core__/components/navigation/tabs/Tabs.vue'
import useTabNavigation from '@/__core__/composables/useTabNavigation'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Scorers from '@/widgets/livescore/components/Scorers.vue'

const emit = defineEmits(['tab-changed'])

const props = defineProps({
  header: Object,
  langId: String,
  tabs: {
    type: Array,
    default: () => []
  },
  width: Number,
  disableTabs: Boolean,
  scorers: {
    type: Array,
    default: () => []
  },
  hasScorersSkeleton: {
    type: Boolean,
    default: false
  },
  hasShadow: {
    type: Boolean,
    default: true
  },
  hasCompetition: {
    type: Boolean,
    default: true
  }
})

const status = inject('status', ref(''))
const hasScorers = computed(() => status.value !== 'loading' && !props.header?.upcoming && props.scorers?.length)

const { initialize: initTabs, tabs: navigation, changeTab } = useTabNavigation([], props.langId)

function emitTabClicked(tab) {
  emit('tab-changed', tab)
  changeTab(tab)
}

const unwatch = watch(() => props.tabs, initTabs)

onMounted(() => {
  initTabs(props.tabs)
})

onBeforeUnmount(unwatch)
</script>

<template>
  <div class="szw-flex szw-flex-col swz-w-full szw-items-center szw-gap-1 szw-rounded szw-py-1" :style="{ boxShadow: hasShadow ? '0px 3px 4px 0px #00000026' : 'none' }">
    <remake-header :header-data="header" :has-shadow="false" :size="width" :has-padding="false" :has-competition="hasCompetition" />

    <tabs
      v-if="!disableTabs && navigation.length"
      :tabs="navigation"
      :tabs-number="navigation.length"
      @tab-clicked="emitTabClicked"
      :classes="'szw-w-full szw-justify-center'"
      class="szw-w-full szw-max-w-[768px] szw-mx-auto szw-min-h-[32px] szw-max-h-[32px] szw-h-[32px] szw-px-1"
    />

    <div v-if="hasScorers" class="szw-flex szw-flex-col szw-w-full szw-max-w-[768px] szw-mx-auto szw-gap-[4px]">
      <scorers :scorers="scorers" :size="width" />
    </div>

    <div v-else-if="status === 'loading' && hasScorersSkeleton" class="szw-flex szw-flex-col szw-w-full szw-h-fit szw-mx-auto szw-gap-1 szw-p-2">
      <div class="szw-flex szw-items-center szw-justify-center szw-space-x-[10px] szw-pt-[6px]">
        <span class="szw-flex-grow szw-h-px szw-bg-border" />
        <span class="szw-h-3 szw-w-[100px] szw-bg-background-color-secondary szw-rounded-0.5" />
        <span class="szw-flex-grow szw-h-px szw-bg-border" />
      </div>

      <div class="szw-flex szw-w-full szw-items-center szw-justify-between" v-for="scorer of scorers" :key="scorer.id">
        <span v-if="scorer.home" class="szw-h-3 szw-w-[100px] szw-bg-background-color-secondary szw-rounded-0.5" />
        <span v-if="scorer.away" class="szw-h-3 szw-w-[100px] szw-bg-background-color-secondary szw-rounded-0.5 szw-ml-auto" />
      </div>
    </div>
  </div>
</template>
